import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Search, Check, X, RefreshCw, Shield, Eye, EyeOff } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';

const mockLiveModels = [
  { id: 'gpt-4-turbo-2024', name: 'GPT-4 Turbo (Latest)', provider: 'OpenAI', type: 'paid', pricing: '$0.01/1K', context: '128k', speed: 'fast', status: 'pending', addedDate: '2024-01', popularity: 95 },
  { id: 'claude-3-opus-20240229', name: 'Claude 3 Opus', provider: 'Anthropic', type: 'paid', pricing: '$0.015/1K', context: '200k', speed: 'medium', status: 'pending', addedDate: '2024-03', popularity: 92 },
  { id: 'gemini-1.5-pro-latest', name: 'Gemini 1.5 Pro', provider: 'Google', type: 'paid', pricing: '$0.007/1K', context: '1M', speed: 'medium', status: 'pending', addedDate: '2024-05', popularity: 88 },
  { id: 'llama-3.1-405b', name: 'Llama 3.1 405B', provider: 'Meta', type: 'free', pricing: 'Open source', context: '128k', speed: 'fast', status: 'pending', addedDate: '2024-07', popularity: 85 }
];

const AdminModels = () => {
  const [pendingModels, setPendingModels] = useState(mockLiveModels);
  const [approvedModels, setApprovedModels] = useState([]);
  const [rejectedModels, setRejectedModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { isAdmin } = useAuth();

  if (!isAdmin()) {
    return <Navigate to="/" replace />;
  }

  const fetchLatestModels = async () => {
    setLoading(true);
    toast({
      title: 'Fetching Latest Models',
      description: 'Scanning OpenRouter for new AI models...',
    });

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/fetch-new-models`);
      const data = await response.json();
      
      if (data.success) {
        setPendingModels(data.models);
        toast({
          title: 'Models Updated!',
          description: `Found ${data.models.length} new models for review`,
        });
      }
    } catch (error) {
      console.error('Error fetching models:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch models. Using cached data.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = (model, visibility) => {
    const approvedModel = { ...model, status: 'approved', visibility };
    setApprovedModels(prev => [...prev, approvedModel]);
    setPendingModels(prev => prev.filter(m => m.id !== model.id));
    toast({ title: 'Model Approved' });
  };

  const handleReject = (model) => {
    setRejectedModels(prev => [...prev, { ...model, status: 'rejected' }]);
    setPendingModels(prev => prev.filter(m => m.id !== model.id));
    toast({ title: 'Model Rejected' });
  };

  const ModelCard = ({ model, showActions = true }) => (
    <Card className="bg-gray-800/50 border-gray-700 p-4">
      <div className="flex justify-between mb-3">
        <div>
          <h3 className="text-lg font-semibold text-white">{model.name}</h3>
          <p className="text-sm text-gray-400">{model.provider}</p>
        </div>
        <Badge className={model.type === 'free' ? 'bg-green-500/20 text-green-400' : 'bg-purple-500/20 text-purple-400'}>
          {model.type === 'free' ? 'FREE' : 'PAID'}
        </Badge>
      </div>
      
      {showActions && model.status === 'pending' && (
        <div className="flex gap-2 pt-3 border-t border-gray-700">
          <Button onClick={() => handleApprove(model, 'public')} size="sm" className="flex-1 bg-green-600">
            Approve Public
          </Button>
          <Button onClick={() => handleApprove(model, 'private')} size="sm" variant="outline" className="flex-1">
            Private
          </Button>
          <Button onClick={() => handleReject(model)} size="sm" variant="outline" className="border-red-600 text-red-400">
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}
    </Card>
  );

  return (
    <div className="min-h-screen py-8 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Admin: Model Management</h1>
          <p className="text-gray-400">Review and approve new AI models</p>
        </div>

        <Button onClick={fetchLatestModels} disabled={loading} className="mb-6 bg-blue-600">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Fetch Latest Models
        </Button>

        <Tabs defaultValue="pending">
          <TabsList className="bg-gray-800/50 border border-gray-700 mb-6">
            <TabsTrigger value="pending">Pending ({pendingModels.length})</TabsTrigger>
            <TabsTrigger value="approved">Approved ({approvedModels.length})</TabsTrigger>
            <TabsTrigger value="rejected">Rejected ({rejectedModels.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="pending">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {pendingModels.map(model => (
                <ModelCard key={model.id} model={model} />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="approved">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {approvedModels.map(model => (
                <ModelCard key={model.id} model={model} showActions={false} />
              ))}
            </div>
          </TabsContent>

          <TabsContent value="rejected">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {rejectedModels.map(model => (
                <ModelCard key={model.id} model={model} showActions={false} />
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminModels;
